import { useState, useEffect, useContext } from 'react';
import {
    Box,
    Paper,
    Avatar,
    Grid,
    Typography,
    Alert,
    TextField,
} from '@mui/material';
import {
    PersonOutline,
    Add,
    Close,
    Euro,
    ConfirmationNumber,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Hidden from '../../../components/Hidden';
import {
    AppContext,
    SttButton,
    SttSelectMshipDialog,
    SttFormSwitch,
} from '../../../sporttia/all';
import {
    deleteElementFromArray,
    updateElementInArray,
    formatPriceByLocale,
} from '../../../lib/utils';
import BookingSelectRateDialog from './BookingSelectRateDialog';
import BookingSelectPupilsDialog from './BookingSelectPupilsDialog';
import RateAndBoletos from './RateAndBoletos';
import constants from '../../../config/constants';
/**
 *	Selecting the occupants
 *	-----------------------
 *
 *	We've got the next scenario:
 *
 *		1) I'm user and it's a capacity booking -> checklist
 *		2) I'm user and it's NOT a capacity booking -> just a SttFormText with my name
 *		3) I'm sports center and it's a capacity booking -> SttList with selected users
 *		4) I'm sports center and it's NOT a capacity booking -> SttSelectMship
 *
 */

const Scenarios = {
    USER_CAPACITY: 1,
    USER_NO_CAPACITY: 2,
    SC_CAPACITY: 3,
    SC_NO_CAPACITY: 4,
};

export default function BookingSectionOccupants({
    timeini,
    timeend,
    piece,
    column,
    rateType,
    setRateType,
    sc,
    ImSCOwner,
    occupants,
    setOccupants,
    isForGame,
    forceFullRates = false,
    generateOccupantUnlocker,
}: any) {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [scenario, setScenario] = useState(0);
    // Secuencia para dar id único a cada línea de entradas impersonales
    // (todas apuntan al mismo usuario invitado, así que su user.id no sirve).
    const [guestSeq, setGuestSeq] = useState(0);

    const [occupantMshipSelecting, setOccupantMshipSelecting] =
        useState<any>(null);
    const [openSelectingRateOccupant, setOpenSelectingRateOccupant] =
        useState<any>(null);
    const [openPupilsDialog, setOpenPupilsDialog] = useState(false);

    const isUserOrTeacher = cxt.user?.role !== constants.roles.sportcenter;

    useEffect(() => {
        // Si forceFullRates es true (dentro del período de cancelación), forzar tarifas completas
        if (forceFullRates && rateType !== cxt.constants.RT_Complete) {
            setRateType(cxt.constants.RT_Complete);
        }
        // Si es un partido normal (no dentro del período de cancelación), usar tarifas individuales
        else if (
            isForGame &&
            !forceFullRates &&
            rateType !== cxt.constants.RT_Individual
        ) {
            setRateType(cxt.constants.RT_Individual);
        }
    }, [
        isForGame,
        forceFullRates,
        rateType,
        cxt.constants.RT_Individual,
        cxt.constants.RT_Complete,
        setRateType,
    ]);

    useEffect(() => {
        if (occupants) {
            // For each of the two rate types, we check if each user has rates of the appropriate type and set it to null otherwise
            let ratesKey;
            let altRatesKey;
            // Si forceFullRates es true, siempre usar tarifas completas
            if (forceFullRates) {
                ratesKey = 'fullBonos';
                altRatesKey = 'fullRates';
            } else if (isForGame) {
                ratesKey = 'individualBonos';
                altRatesKey = 'individualRates';
            } else if (rateType === cxt.constants.RT_Individual) {
                ratesKey = 'individualBonos';
                altRatesKey = 'individualRates';
            } else {
                ratesKey = 'fullBonos';
                altRatesKey = 'fullRates';
            }

            // fullBonos, individualBonos

            const occupantsUpdated = occupants.map((occupant: any) => {
                let selectedRate = null;
                let selectedBoleto = null;

                const fares = occupant.fares || {};

                if (fares[ratesKey]) {
                    selectedRate = null;
                    const [firstBoleto] = fares[ratesKey];
                    selectedBoleto = firstBoleto;
                } else if (fares[altRatesKey]) {
                    const [firstRate] = fares[altRatesKey];
                    selectedRate = firstRate;
                    selectedBoleto = null;
                }

                return {
                    ...occupant,
                    selectedRate,
                    selectedBoleto,
                };
            });

            setOccupants(occupantsUpdated);
        }
    }, [rateType]);

    /**
     * Load fares
     *
     * 	fares: {
     * 		individualRates: [{
     *			total, lowest,
     *			prices: [ { id, name, duration, price }, ... ]
     *		}, ... ]
     * 		fullRates: ... the same
     * 		individualBonos: [{ id, amount, consumed, bono: {id, name, type} }]
     * 		fullBonos: ... the same
     * 	}
     */
    const loadFares = (occupant: any) => {
        if (occupant && occupant.id) {
            cxt.api('GET', '/bookings/fares', {
                params: {
                    ini: timeini,
                    end: timeend,
                    idFacility: column.facility.id,
                    idUser: occupant.user.id,
                },
                success(r) {
                    const fares = r as any;

                    if (fares) {
                        const updatedOccupant = {
                            ...occupant,
                            fares,
                        };

                        const {
                            individualBonos = [],
                            individualRates = [],
                            fullBonos = [],
                            fullRates = [],
                        } = fares;

                        // Si forceFullRates es true, solo usar tarifas completas
                        if (forceFullRates) {
                            if (fullBonos.length > 0) {
                                const [firstBono] = fullBonos;
                                updatedOccupant.selectedBoleto = firstBono;
                                updatedOccupant.selectedRate = null;
                            } else if (fullRates.length > 0) {
                                const [firstRate] = fullRates;
                                updatedOccupant.selectedRate = firstRate;
                                updatedOccupant.selectedBoleto = null;
                            } else {
                                cxt.showMessage(
                                    'I',
                                    t('booking.noAvailableRates'),
                                );
                                setOccupants(
                                    deleteElementFromArray(occupants, occupant),
                                );
                                setOccupantMshipSelecting({} as any);
                                return;
                            }
                        } else if (isForGame) {
                            if (individualBonos.length > 0) {
                                const [firstBono] = individualBonos;
                                updatedOccupant.selectedBoleto = firstBono;
                                updatedOccupant.selectedRate = null;
                            } else if (individualRates.length > 0) {
                                const [firstRate] = individualRates;
                                updatedOccupant.selectedRate = firstRate;
                                updatedOccupant.selectedBoleto = null;
                            } else {
                                cxt.showMessage(
                                    'I',
                                    t('booking.noAvailableRates'),
                                );
                                setOccupants(
                                    deleteElementFromArray(occupants, occupant),
                                );
                                setOccupantMshipSelecting({} as any);
                                return;
                            }
                        }
                        // Lógica original para no partidos
                        else if (rateType === cxt.constants.RT_Individual) {
                            if (individualBonos.length > 0) {
                                const [firstBono] = individualBonos;
                                updatedOccupant.selectedBoleto = firstBono;
                                updatedOccupant.selectedRate = null;
                            } else if (individualRates.length > 0) {
                                const [firstRate] = individualRates;
                                updatedOccupant.selectedRate = firstRate;
                                updatedOccupant.selectedBoleto = null;
                            } else if (fullBonos.length > 0) {
                                const [firstBono] = fullBonos;
                                setRateType(cxt.constants.RT_Complete);
                                updatedOccupant.selectedBoleto = firstBono;
                                updatedOccupant.selectedRate = null;
                            } else if (fullRates.length > 0) {
                                const [firstRate] = fullRates;
                                setRateType(cxt.constants.RT_Complete);
                                updatedOccupant.selectedRate = firstRate;
                                updatedOccupant.selectedBoleto = null;
                            }
                        } else if (fullBonos.length > 0) {
                            const [firstBono] = fullBonos;
                            updatedOccupant.selectedBoleto = firstBono;
                            updatedOccupant.selectedRate = null;
                        } else if (fullRates.length > 0) {
                            const [firstRate] = fullRates;
                            updatedOccupant.selectedRate = firstRate;
                            updatedOccupant.selectedBoleto = null;
                        } else if (individualBonos.length > 0) {
                            const [firstBono] = individualBonos;
                            setRateType(cxt.constants.RT_Individual);
                            updatedOccupant.selectedBoleto = firstBono;
                            updatedOccupant.selectedRate = null;
                        } else if (individualRates.length > 0) {
                            const [firstRate] = individualRates;
                            setRateType(cxt.constants.RT_Individual);
                            updatedOccupant.selectedRate = firstRate;
                            updatedOccupant.selectedBoleto = null;
                        }

                        // Update occupants
                        if (
                            individualRates.length > 0 ||
                            fullRates.length > 0 ||
                            individualBonos.length > 0 ||
                            fullBonos.length > 0
                        ) {
                            setOccupants(
                                updateElementInArray(
                                    occupants,
                                    updatedOccupant,
                                ),
                            );
                        } else {
                            cxt.showMessage('I', t('booking.noAvailableRates'));
                            setOccupants(
                                deleteElementFromArray(occupants, occupant),
                            );
                            if (ImSCOwner) {
                                setOccupantMshipSelecting({} as any);
                            }
                        }
                    }
                },
            });
        }
    };

    /**
     * Times change, we've got to recalculate fares
     */
    useEffect(() => {
        // Load all fares of all occupants
        if (timeini && timeend && occupants) {
            occupants.forEach((occupant: any) => {
                loadFares(occupant);
            });
        }
    }, [timeini, timeend]);

    /**
     * Update the occupant with not fares
     */
    useEffect(() => {
        occupants.forEach((occupant: any) => {
            if (!occupant.fares) {
                loadFares(occupant);
            }
        });
    }, [occupants]);

    /**
     * Select the current scenario
     */
    useEffect(() => {
        const isWithCapacity = piece.capacity && piece.capacity.total > 0;

        // Set scenario
        if (ImSCOwner) {
            setScenario(
                isWithCapacity
                    ? Scenarios.SC_CAPACITY
                    : Scenarios.SC_NO_CAPACITY,
            );
        } else {
            setScenario(
                isWithCapacity
                    ? Scenarios.USER_CAPACITY
                    : Scenarios.USER_NO_CAPACITY,
            );
        }
    }, [ImSCOwner, cxt.user, piece]);

    /**
     * Secenairo is just set
     */
    useEffect(() => {
        if (scenario) {
            switch (scenario) {
                // Just enter show the selecing mship dialog to choose one
                case Scenarios.SC_CAPACITY:
                case Scenarios.SC_NO_CAPACITY:
                    setOccupantMshipSelecting({} as any);
                    break;
                default:
                    break;
            }
        }
    }, [scenario]);

    /**
     * Add / updateoccupant
     */
    const addSaveOccupant = (occupant: any) => {
        // Set default id for the occupant
        if (!occupant.id) {
            occupant.id = occupant.user.id;
        }

        occupant.name = occupant.guest
            ? t('impersonalTicket.guestEntry')
            : ImSCOwner
              ? occupant.mship?.fullName
              : occupant.user?.fullName;

        // Update occupants
        setOccupants(updateElementInArray(occupants, occupant));
    };

    /**
     * Delete occupan
     */
    const delOccupant = (occupant: any) => {
        setOccupants(deleteElementFromArray(occupants, occupant));
    };

    /**
     * Add an impersonal (guest) entry line: bound to the center's guest user,
     * with its own rate and a quantity that the backend expands into N entries.
     * Several lines allow multi-tarifa (adults / kids).
     */
    const addGuestOccupant = () => {
        const guestUser = sc?.guestUser;
        if (!guestUser) {
            cxt.showMessage('E', t('impersonalTicket.noGuestUser'));
            return;
        }

        const seq = guestSeq + 1;
        setGuestSeq(seq);

        addSaveOccupant({
            id: `guest-${seq}`,
            guest: true,
            quantity: 1,
            mship: guestUser.mship,
            user: {
                id: guestUser.id,
                fullName: guestUser.mship?.fullName ?? guestUser.fullName,
            },
        });
    };

    /**
     * Init
     */
    useEffect(() => {
        // If I'm a user the get my mship (relation between sports center and me)
        if (isUserOrTeacher) {
            cxt.api('GET', `/scs/${sc.id}/me`, {
                success: (r: any) => {
                    // Set first occupant, me
                    addSaveOccupant({
                        name: r.mship.fullName
                            ? r.mship.fullName
                            : r.mship.user.fullName,
                        mship: r.mship,
                        user: cxt.user,
                    });
                },
            });
        }
    }, []);

    // -----| Render |-----
    return (
        <Box mt={2}>
            {occupants &&
                occupants.map((occupant: any) => (
                    <Box mt={2} key={occupant.id}>
                        <Paper>
                            {[
                                Scenarios.USER_NO_CAPACITY,
                                Scenarios.SC_NO_CAPACITY,
                            ].includes(scenario) &&
                                !isForGame &&
                                !forceFullRates && (
                                    <Box
                                        p={3}
                                        display="flex"
                                        justifyContent="flex-end"
                                    >
                                        <SttFormSwitch
                                            caption={t(
                                                'booking.individualRates',
                                            )}
                                            checked={
                                                rateType ===
                                                cxt.constants.RT_Individual
                                            }
                                            onChange={({ value }: any) =>
                                                setRateType(
                                                    value
                                                        ? cxt.constants
                                                              .RT_Individual
                                                        : cxt.constants
                                                              .RT_Complete,
                                                )
                                            }
                                        />
                                    </Box>
                                )}
                            <Box p={3}>
                                <Grid container spacing={3}>
                                    <Hidden smDown>
                                        <Grid
                                            sx={{ textAlign: 'center' }}
                                            size={{ md: 2, sm: 12, xs: 12 }}
                                        >
                                            <Box sx={{ textAlign: 'center' }}>
                                                <Avatar
                                                    alt={occupant.name}
                                                    src="/static/images/avatar/1.jpg"
                                                />
                                            </Box>
                                        </Grid>
                                    </Hidden>

                                    <Grid size={{ md: 10, sm: 12, xs: 12 }}>
                                        <Typography variant="h5">
                                            {occupant.name}
                                        </Typography>

                                        {occupant.mship &&
                                            occupant.mship.amount > 0 && (
                                                <Box>
                                                    {t('payment.purse')}:{' '}
                                                    <b>
                                                        {formatPriceByLocale(
                                                            occupant.mship
                                                                ?.amount,
                                                            occupant.mship
                                                                ?.amountShape
                                                                ?.currency,
                                                            occupant.mship
                                                                ?.amountShape
                                                                ?.locale,
                                                        )}
                                                    </b>
                                                </Box>
                                            )}

                                        <RateAndBoletos occupant={occupant} />

                                        {/* Buttons */}
                                        <Box mt={3}>
                                            <Grid
                                                container
                                                spacing={3}
                                                alignItems="center"
                                            >
                                                {occupant.user && (
                                                    <Grid
                                                        size={{
                                                            xs: 'auto',
                                                        }}
                                                    >
                                                        <SttButton
                                                            size="small"
                                                            startIcon={
                                                                piece.priceShape
                                                                    ?.currency !==
                                                                'EUR' ? (
                                                                    piece
                                                                        .priceShape
                                                                        ?.currency
                                                                ) : (
                                                                    <Euro />
                                                                )
                                                            }
                                                            caption={t(
                                                                'booking.selectRate',
                                                            )}
                                                            onClick={() =>
                                                                setOpenSelectingRateOccupant(
                                                                    occupant,
                                                                )
                                                            }
                                                        />
                                                    </Grid>
                                                )}

                                                {occupant.guest && (
                                                    <Grid size={{ xs: 'auto' }}>
                                                        <TextField
                                                            size="small"
                                                            type="number"
                                                            label={t(
                                                                'impersonalTicket.quantity',
                                                            )}
                                                            value={
                                                                occupant.quantity ??
                                                                1
                                                            }
                                                            slotProps={{
                                                                htmlInput: {
                                                                    min: 1,
                                                                },
                                                            }}
                                                            onChange={(e) =>
                                                                addSaveOccupant(
                                                                    {
                                                                        ...occupant,
                                                                        quantity:
                                                                            Math.max(
                                                                                1,
                                                                                parseInt(
                                                                                    e
                                                                                        .target
                                                                                        .value,
                                                                                    10,
                                                                                ) ||
                                                                                    1,
                                                                            ),
                                                                    },
                                                                )
                                                            }
                                                            sx={{ width: 110 }}
                                                        />
                                                    </Grid>
                                                )}

                                                {[
                                                    Scenarios.SC_CAPACITY,
                                                    Scenarios.SC_NO_CAPACITY,
                                                ].includes(scenario) &&
                                                    !occupant.guest && (
                                                        <Grid
                                                            size={{
                                                                xs: 'auto',
                                                            }}
                                                        >
                                                            <SttButton
                                                                size="small"
                                                                startIcon={
                                                                    <PersonOutline />
                                                                }
                                                                caption={t(
                                                                    'booking.selectUser',
                                                                )}
                                                                onClick={() =>
                                                                    setOccupantMshipSelecting(
                                                                        occupant,
                                                                    )
                                                                }
                                                            />
                                                        </Grid>
                                                    )}

                                                {([
                                                    Scenarios.USER_CAPACITY,
                                                    Scenarios.SC_CAPACITY,
                                                ].includes(scenario) ||
                                                    occupant.guest) && (
                                                    <Grid
                                                        size={{
                                                            xs: 'auto',
                                                        }}
                                                    >
                                                        <SttButton
                                                            size="small"
                                                            color="error"
                                                            startIcon={
                                                                <Close />
                                                            }
                                                            caption={t(
                                                                'general.delete',
                                                            )}
                                                            onClick={() =>
                                                                delOccupant(
                                                                    occupant,
                                                                )
                                                            }
                                                        />
                                                    </Grid>
                                                )}

                                                {occupant.selectedRate && (
                                                    <Grid
                                                        size={{ xs: 'auto' }}
                                                        style={{ flexGrow: 1 }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                textAlign:
                                                                    'right',
                                                            }}
                                                            style={{
                                                                fontSize: 18,
                                                            }}
                                                        >
                                                            {t('payment.total')}
                                                            :{' '}
                                                            <b>
                                                                {formatPriceByLocale(
                                                                    (occupant
                                                                        ?.selectedRate
                                                                        ?.total ??
                                                                        0) *
                                                                        (occupant.quantity ??
                                                                            1),
                                                                    occupant
                                                                        ?.selectedRate
                                                                        ?.prices[0]
                                                                        ?.priceShape
                                                                        .currency,
                                                                    occupant
                                                                        ?.selectedRate
                                                                        ?.prices[0]
                                                                        ?.priceShape
                                                                        ?.locale,
                                                                )}
                                                            </b>
                                                        </Box>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Paper>
                    </Box>
                ))}

            {occupants &&
                occupants.length === 0 &&
                !(isForGame && ImSCOwner) && (
                    <Alert severity="warning">
                        {t('booking.youMustSelectOneOccupant')}
                    </Alert>
                )}

            {!isForGame &&
                ([Scenarios.USER_CAPACITY, Scenarios.SC_CAPACITY].includes(
                    scenario,
                ) ||
                    (scenario === Scenarios.SC_NO_CAPACITY &&
                        !occupants.length)) && (
                    <Box mt={2}>
                        <SttButton
                            fullWidth
                            startIcon={<Add />}
                            caption={t('booking.addUserNotInTheList')}
                            onClick={() => {
                                if (ImSCOwner) {
                                    setOccupantMshipSelecting({} as any);
                                } else {
                                    setOpenPupilsDialog(true);
                                }
                            }}
                        />
                    </Box>
                )}

            {/* Entradas impersonales: solo el centro, y solo si la instalación
                genera llaves QR por ocupante (activación híbrida por venta). */}
            {ImSCOwner &&
                !isForGame &&
                generateOccupantUnlocker === constants.unlocker.types.QR && (
                    <Box mt={2}>
                        <SttButton
                            fullWidth
                            color="secondary"
                            startIcon={<ConfirmationNumber />}
                            caption={t('impersonalTicket.addGuests')}
                            onClick={addGuestOccupant}
                        />
                    </Box>
                )}

            <SttSelectMshipDialog
                open={occupantMshipSelecting !== null}
                onClose={() => setOccupantMshipSelecting(null)}
                onSelectMship={(mship: any) => {
                    addSaveOccupant({
                        id: occupantMshipSelecting?.id || mship.user?.id,
                        name: ImSCOwner ? mship.fullName : mship.user.fullName,
                        mship,
                        user: mship.user,
                    });

                    // Close popup
                    setOccupantMshipSelecting(null);
                }}
            />

            <BookingSelectRateDialog
                open={openSelectingRateOccupant !== null}
                fares={
                    openSelectingRateOccupant && openSelectingRateOccupant.fares
                }
                rateType={rateType}
                onClose={() => setOpenSelectingRateOccupant(null)}
                onSelect={({ rate, boleto }: any) => {
                    if (openSelectingRateOccupant) {
                        addSaveOccupant({
                            ...openSelectingRateOccupant,
                            selectedRate: !boleto && rate,
                            selectedBoleto: boleto,
                        });
                    }
                }}
                occupants={occupants}
                isForGame={isForGame}
                forceFullRates={forceFullRates}
            />
            {isUserOrTeacher && (
                <BookingSelectPupilsDialog
                    open={openPupilsDialog}
                    sc={sc}
                    occupants={occupants}
                    onClose={() => setOpenPupilsDialog(false)}
                    onAddOccupant={addSaveOccupant}
                />
            )}
        </Box>
    );
}
